import React, { useState } from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import {
  IScene,
  ISelectedCustomScenes
} from '../../../types/Scenes/scenesTypes';
import Strings from '../../../i18n';

interface CustomLightShowProps {
  scenes: IScene[];
  selectedScenes: ISelectedCustomScenes[];
  setSelectedScenes: (selectedScenes: ISelectedCustomScenes[]) => void;
}

const TransitionStylesIds = [
  {
    id: 'NO_TRANSITION',
    name: Strings.lightShows.noTransition
  },
  {
    id: 'BLEND',
    name: Strings.lightShows.blend
  },
  {
    id: 'FADE',
    name: Strings.lightShows.fadeInFadeOut
  }
];

export const CustomLightShow = ({
  scenes,
  selectedScenes,
  setSelectedScenes
}: CustomLightShowProps) => {
  const [selectedScene, setSelectedScene] = useState<number>(0);

  const [sceneDuration, setSceneDuration] = useState({
    seconds: 0,
    milliseconds: 5
  });

  const [transitionDuration, setTransitionDuration] = useState({
    seconds: 0,
    milliseconds: 0
  });

  const [transitionStyle, setTransitionStyle] = useState('No Transition');

  const handleAddScene = () => {
    if (selectedScene) {
      const sceneData = scenes.find((scene) => scene.sceneId === selectedScene);
      if (sceneData && sceneData.sceneId !== undefined) {
        const scene: ISelectedCustomScenes = {
          name: sceneData.name,
          sceneId: sceneData.sceneId,
          transitionType:
            TransitionStylesIds.find((f) => f.name === transitionStyle)?.id ||
            '',
          transitionDuration:
            transitionDuration.seconds + transitionDuration.milliseconds,
          duration: sceneDuration.seconds + sceneDuration.milliseconds
        };
        setSelectedScenes([...selectedScenes, { ...scene }]);
        setSelectedScene(0);
      }
    }
  };

  const handleDeleteScene = (index: number) => {
    const newScenes = selectedScenes.filter((_, i) => i !== index);
    setSelectedScenes(newScenes);
  };

  const onChangeSceneDuration = (value: {
    seconds: number;
    milliseconds: number;
  }) => {
    setSceneDuration(value);
    if (selectedScenes.length > 0) {
      const newScenesData = selectedScenes.map(
        (scene: ISelectedCustomScenes) => {
          return {
            ...scene,
            duration: value.seconds + value.milliseconds / 10
          };
        }
      );
      setSelectedScenes(newScenesData);
    }
  };

  const onChangeTransitionDuration = (value: {
    seconds: number;
    milliseconds: number;
  }) => {
    setTransitionDuration(value);
    if (selectedScenes.length > 0) {
      const newScenesData = selectedScenes.map(
        (scene: ISelectedCustomScenes) => {
          return {
            ...scene,
            transitionDuration: value.seconds + value.milliseconds / 10
          };
        }
      );
      setSelectedScenes(newScenesData);
    }
  };

  const onChangeTransitionStyle = (value: string) => {
    setTransitionStyle(value);
    const newScenesData = selectedScenes.map((scene: ISelectedCustomScenes) => {
      return {
        ...scene,
        transitionType:
          TransitionStylesIds.find((f) => f.name === value)?.id || '',
        transitionDuration:
          value === Strings.lightShows.noTransition
            ? 0
            : scene.transitionDuration
      };
    });
    setSelectedScenes(newScenesData);
    if (value === Strings.lightShows.noTransition) {
      setTransitionDuration({ seconds: 0, milliseconds: 0 });
    }
  };

  const renderDurationInput = (
    value: { seconds: number; milliseconds: number },
    onChange: (newValue: { seconds: number; milliseconds: number }) => void
  ) => (
    <div className="form-group">
      <div className="row">
        <div className="col">
          <input
            id="sceneDurationSeconds"
            type="number"
            value={value.seconds}
            onChange={(e) =>
              Number(e.target.value) >= 0 &&
              onChange({ ...value, seconds: Number(e.target.value) })
            }
            className="form-control"
          />
          <small className="form-text text-muted">SEC</small>
        </div>
        <div className="col">
          <input
            type="number"
            value={value.milliseconds}
            onChange={(e) =>
              Array.from({ length: 10 }, (_, i) => i + 1).includes(
                Number(e.target.value)
              ) && onChange({ ...value, milliseconds: Number(e.target.value) })
            }
            className="form-control"
          />
          <small className="form-text text-muted">1/10</small>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h5 className="text-center mb-4">* CUSTOM LIGHT SHOW SETTINGS</h5>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="p-3 border rounded">
            <div className="mb-3">
              <h5>Scene Duration</h5>
              {renderDurationInput(sceneDuration, onChangeSceneDuration)}
            </div>
            <div className="mb-3">
              <h5>Select Transition</h5>
              <select
                value={transitionStyle}
                onChange={(e) => onChangeTransitionStyle(e.target.value)}
                className="form-control"
              >
                <option>No Transition</option>
                <option>Blend</option>
                <option>Fade In / Fade Out</option>
              </select>
            </div>
            {transitionStyle !== 'No Transition' && (
              <div className="mb-3">
                <h5>Transition Duration</h5>
                {renderDurationInput(
                  transitionDuration,
                  onChangeTransitionDuration
                )}
              </div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="p-3 border rounded">
            <h5 className="mb-3">Scenes</h5>

            <div className="d-flex align-items-stretch gap-3 mb-3">
              <div className="flex-grow-1">
                <Dropdown
                  value={selectedScene}
                  options={scenes}
                  onChange={(e: DropdownChangeEvent) =>
                    setSelectedScene(e.value)
                  }
                  optionLabel="name"
                  optionValue="sceneId"
                  placeholder="Select a Scene"
                  className="w-100"
                />
              </div>
              <button
                type="button"
                onClick={handleAddScene}
                disabled={scenes.length >= 5}
                className="btn btn-primary w-auto h-100"
              >
                +
              </button>
            </div>
            {selectedScenes.map((scene, index) => (
              <div key={index} className="input-group mb-2">
                <input
                  type="text"
                  value={scene.name}
                  disabled
                  className="form-control"
                />
                {index >= 1 && (
                  <div className="input-group-append">
                    <button
                      type="button"
                      onClick={() => handleDeleteScene(index)}
                      className="btn btn-danger"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
